import React, { useEffect, useState } from "react";
import { toggleElementFullScreen } from "../../utils/widgetExport";
import DownloadMenu from "../common/DownloadMenu";
import "./widgetHeader.css";

/* Is this widget the element the browser is currently showing full screen?
   The button toggles, so it has to say which way it is about to go - and the state can
   change without it being pressed (Esc, F11, another widget expanding), which is why it
   is read off the document rather than remembered locally. */
const useIsFullScreen = (targetRef) => {
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    const sync = () => {
      const current = document.fullscreenElement || document.webkitFullscreenElement;
      const element = targetRef?.current;
      setIsFullScreen(Boolean(current && element && (current === element || current.contains(element))));
    };

    sync();
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, [targetRef]);

  return isFullScreen;
};

/**
 * The bar across the top of one widget on a published board: its name, and the actions
 * that widget was configured to offer.
 *
 * Which buttons appear comes from the widget's own `properties`, not the board's. On a
 * dashboard several widgets sit side by side, so a single board-wide download button
 * could not say which table it meant - each widget carries its own. Refresh and auto
 * refresh are NOT here on purpose: both reload the page, so they stay board-level and
 * live in the board header.
 *
 * A report holds one widget, so its widget header gets no properties at all and only the
 * board toolbar is shown - unchanged from before.
 */
export default function WidgetHeader({
  title,
  rowCount,
  properties,
  onDownload,
  onExport,
  fullScreenTargetRef,
  alwaysShow,
}) {
  const isFullScreen = useIsFullScreen(fullScreenTargetRef);
  const widgetProperties = properties || {};
  /* Two shapes of download, and which one appears depends on what the caller handed in.

     onExport(format) is the menu - PDF / PNG / XLS always, CSV when the widget's
     csvdownload property allows it. Withholding every way of getting the data out is not
     a display preference, so the button is not behind a flag; only the CSV entry is.

     onDownload is the older CSV-only button, still used by the scheduled report page. */
  const showMenu = typeof onExport === "function";
  const showDownload = !showMenu && Boolean(widgetProperties.csvdownload) && typeof onDownload === "function";
  const showFullScreen = Boolean(widgetProperties.fullscreen);
  const hasTitle = Boolean(title);

  /* Nothing to put in the bar and nothing asking for it: an empty strip would just be a
     gap above the widget.

     `alwaysShow` is for charts, which keep their header even when the widget has no name
     yet and no actions - a chart with no label above it is unidentifiable, where a table
     still announces itself through its column headers. */
  if (!alwaysShow && !hasTitle && !showMenu && !showDownload && !showFullScreen) return null;

  return (
    <div className="widget-header">
      <h5 className="widget-header-title">{title ?? ""}</h5>

      {/* How many rows this table is holding, after its filters. The pager says the same
          thing at the foot of the table, but only once the reader has scrolled to it -
          and on a dashboard of several widgets the count is part of what tells them
          apart. Number.isFinite, so a widget still loading shows nothing rather than
          "NaN total" or a premature "0 total". */}
      {Number.isFinite(rowCount) ? (
        <span className="widget-header-count">{rowCount.toLocaleString()} total</span>
      ) : null}

      {showMenu || showDownload || showFullScreen ? (
        <div className="widget-header-actions">
          {showMenu ? (
            <DownloadMenu
              allowCsv={Boolean(widgetProperties.csvdownload)}
              onSelect={onExport}
              className="widget-header-btn"
            />
          ) : null}

          {showDownload ? (
            <button type="button" className="widget-header-btn" title="Download CSV" onClick={onDownload}>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="15" height="15">
                <path d="M8 2v8m0 0L5 7m3 3l3-3" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2.5 11.5v1a1.5 1.5 0 001.5 1.5h8a1.5 1.5 0 001.5-1.5v-1" strokeLinecap="round" />
              </svg>
            </button>
          ) : null}

          {/* One button, both directions. It only ever showed the "expand" arrows, so
              once a widget filled the screen there was nothing on it saying how to get
              back - the arrows now point inward and the label changes with the state. */}
          {showFullScreen ? (
            <button
              type="button"
              className={`widget-header-btn${isFullScreen ? " widget-header-btn--active" : ""}`}
              title={isFullScreen ? "Exit full screen" : "Full screen"}
              aria-label={isFullScreen ? "Exit full screen" : "Full screen"}
              onClick={() => toggleElementFullScreen(fullScreenTargetRef?.current)}
            >
              {isFullScreen ? (
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="15" height="15">
                  <path d="M2 5h3V2M14 5h-3V2M2 11h3v3M14 11h-3v3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="15" height="15">
                  <path d="M6 2H2v4M10 2h4v4M6 14H2v-4M10 14h4v-4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
